"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.datadogRum = exports.datadogLogs = exports.tabby = void 0;
exports.neverTabby = neverTabby;
exports.dialerBrowserLogger = dialerBrowserLogger;
exports.sipJSBrowserLogger = sipJSBrowserLogger;
exports.dialerV2BrowserLogger = dialerV2BrowserLogger;
exports.callHistoryBrowserLogger = callHistoryBrowserLogger;
const browser_logs_1 = require("@datadog/browser-logs");
Object.defineProperty(exports, "datadogLogs", { enumerable: true, get: function () { return browser_logs_1.datadogLogs; } });
const common_1 = require("./common");
const constants_1 = require("./constants");
const browser_tabby_1 = require("./browser_tabby");
const browser_rum_1 = require("@datadog/browser-rum");
Object.defineProperty(exports, "datadogRum", { enumerable: true, get: function () { return browser_rum_1.datadogRum; } });
// TODO: When we are no longer using web components, use react's constants.ts.
const TESTING = process.env.TESTING ?? "";
const NODE_ENV = TESTING ? constants_1.Env.TEST : process.env.NODE_ENV;
const env = constants_1.zEnv.parse(NODE_ENV);
const DATADOG_CLIENT_TOKEN = process.env.DATADOG_CLIENT_TOKEN;
if (DATADOG_CLIENT_TOKEN === undefined) {
    throw new Error("No datadog client token.");
}
const DATADOG_RUM_APPLICATION_ID = process.env.DATADOG_RUM_APPLICATION_ID;
if (DATADOG_RUM_APPLICATION_ID === undefined) {
    throw new Error("No datadog RUM application ID.");
}
const DATADOG_RUM_CLIENT_TOKEN = process.env.DATADOG_RUM_CLIENT_TOKEN;
if (DATADOG_RUM_CLIENT_TOKEN === undefined) {
    throw new Error("No datadog RUM client token.");
}
const SAPI_BASE_URL = process.env.SAPI_BASE_URL;
if (SAPI_BASE_URL === undefined) {
    throw new Error("No SAPI base URL.");
}
const monitoringEnabled = [constants_1.Env.PLAYGROUND, constants_1.Env.STAGING, constants_1.Env.PROD].includes(env);
function hasRUMDeadClicks(event) {
    if (event.type !== "action" || event.action.frustration === undefined)
        return false;
    return event.action.frustration.type.includes("dead_click");
}
const CLICKABLE_TAGS_SELECTOR = "a, button, input, select, textarea, label, summary, [role='button'], [role='link'], [tabindex]:not([tabindex='-1']), [data-dd-action-name], [data-trk-id]";
function hasClickableElements(context) {
    const pointerEvents = context.events;
    if (pointerEvents === undefined || pointerEvents.length === 0)
        return false;
    return pointerEvents.some(({ pointerType, target }) => pointerType === "mouse" &&
        target instanceof Element &&
        target.matches(CLICKABLE_TAGS_SELECTOR));
}
if (monitoringEnabled) {
    const datadogConfig = {
        service: "browser",
        env,
        site: "datadoghq.com",
        sessionSampleRate: 100,
        version: process.env.ORUM_RELEASE_VERSION ?? `${env}-n/a`,
    };
    browser_rum_1.datadogRum.init({
        ...datadogConfig,
        applicationId: DATADOG_RUM_APPLICATION_ID,
        beforeSend: (event, context) => {
            if (!hasRUMDeadClicks(event))
                return true;
            // If the event is a RUM dead click, it means this context is a RumActionEventDomainContext
            const mouseEvents = context;
            if (hasClickableElements(mouseEvents))
                return true;
            return false;
        },
        clientToken: DATADOG_RUM_CLIENT_TOKEN,
        sessionReplaySampleRate: 100,
        traceSampleRate: 100,
        trackResources: true,
        trackLongTasks: true,
        trackUserInteractions: true,
        defaultPrivacyLevel: "mask-user-input",
        allowedTracingUrls: [window.location.origin, SAPI_BASE_URL],
        enableExperimentalFeatures: ["feature_flags"],
        actionNameAttribute: "data-trk-id",
    });
    browser_logs_1.datadogLogs.init({
        ...datadogConfig,
        beforeSend: event => {
            // Ignore this error; it clouds our logs.
            if (event.type === "error" &&
                event.error?.message?.includes("ResizeObserver loop completed with undelivered notifications.") === true) {
                return false;
            }
            return true;
        },
        clientToken: DATADOG_CLIENT_TOKEN,
        forwardErrorsToLogs: process.env.DATADOG_FORWARD_ERRORS_TO_LOGS === "true",
    });
}
browser_logs_1.datadogLogs.setGlobalContextProperty("host", window.location.host);
// The below properties depend on document.body being available, which is not
// always the case if the code runs early. It can crash the app because `body`
// is null in those cases. Therefore, we wait for the DOM to be loaded before
// we set those properties.
document.addEventListener("DOMContentLoaded", () => {
    browser_logs_1.datadogLogs.setGlobalContextProperty("orgID", window.document.body.getAttribute("data-org-id") ?? undefined);
    const userID = window.document.body.getAttribute("data-id") ?? undefined;
    browser_logs_1.datadogLogs.setGlobalContextProperty("userID", userID);
    browser_logs_1.datadogLogs.setUser({ id: userID });
});
// TODO: This file needs to be moved to react-orum and use the new env vars, but
// can't be moved until the references to the loggers are removed from our
// custom web components.
exports.tabby = (0, browser_tabby_1.getBrowserTabby)({
    monitoringEnabled,
    consoleLoggingEnabled: env === constants_1.Env.DEV,
    version: process.env.ORUM_RELEASE_VERSION ?? `${env}-n/a`,
});
/**
 * Utility function for compile-time exhaustiveness checks and runtime logging.
 *
 * This function takes a `never` argument and logs any value it receives. It is
 * used in conjunction with a switch statement to ensure all cases are handled
 * at compile-time. If an unexpected value is passed at runtime due to type
 * mismatches across service boundaries (which may be running different versions),
 * this function logs the unexpected value.
 */
function neverTabby({ never, message, category, }) {
    exports.tabby.warn({
        category,
        message,
        tags: { never },
    });
}
function browserLogger(args) {
    const { message, category, tags = {}, level, error } = args;
    const messageContext = Object.assign({ category }, tags);
    // TODO: Replace with envSupports("LOG_TO_BROWSER") once moved to react-orum.
    const env = process.env.NODE_ENV ?? constants_1.Env.DEV;
    if ([constants_1.Env.PLAYGROUND, constants_1.Env.DEV].includes(env)) {
        logToConsole(args);
        return;
    }
    browser_logs_1.datadogLogs.logger[level](message, messageContext, error);
}
function dialerBrowserLogger(message, options, error) {
    browserLogger({
        message,
        category: common_1.LogMessageCategory.DIALER,
        ...options,
        error,
    });
}
function sipJSBrowserLogger(message, options, error) {
    browserLogger({
        message,
        category: common_1.LogMessageCategory.SIP_JS,
        ...options,
        error,
    });
}
function dialerV2BrowserLogger(message, options, error) {
    browserLogger({
        message,
        category: common_1.LogMessageCategory.DIALER_V2,
        ...options,
        error,
    });
}
function callHistoryBrowserLogger(message, options, error) {
    browserLogger({
        message,
        category: common_1.LogMessageCategory.CALL_HISTORY,
        ...options,
        error,
    });
}
function logToConsole(args) {
    switch (args.level) {
        case "debug":
            // eslint-disable-next-line no-console
            console.debug(args);
            return;
        case "error":
            // eslint-disable-next-line no-console
            console.error(args);
            return;
        case "warn":
            // eslint-disable-next-line no-console
            console.warn(args);
            return;
        case "info":
        default:
            // eslint-disable-next-line no-console
            console.log(args);
    }
}
//# sourceMappingURL=browser_logs.js.map